import React from 'react';
import { motion } from 'framer-motion';
import SectionHeading from '@/components/brand/SectionHeading';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PrimaryButton } from '@/components/brand/Buttons';
import { fadeUp, staggerParent, viewportOnce } from '@/lib/motion';
import { HOME } from '@/constants/testIds';
import { resolveIcon } from '@/lib/iconMap';

export default function Industries({ data, items }) {
  if (!items?.length) return null;
  const eyebrow = data?.eyebrow;
  const title = data?.title || data?.heading;
  const subtitle = data?.subtitle || data?.subhead;
  const exploreLabel = data?.explore_label;

  const industries = items.map((it) => ({
    ...it,
    icon: resolveIcon(it.icon, ArrowRight),
  }));

  return (
    <section id="industries" className="py-16 bg-white">
      <div className="container mx-auto">

        <SectionHeading
          title={title}
          eyebrow={eyebrow}
          subtitle={subtitle}
          align="center"
          className="mb-12"
        />

        {/* ── Cards grid ── */}
        <motion.div
          data-testid={HOME.industriesGrid}
          variants={staggerParent(0.07)}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {industries.map((it) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.title}
                variants={fadeUp}
                whileHover="hovered"
                initial="rest"
                animate="rest"
                className="group/card relative rounded-2xl bg-white border border-gray-100 shadow-soft overflow-hidden cursor-pointer"
              >
                {/* Accent bar — slides in from left on hover */}
                <motion.div
                  className="absolute top-0 left-0 w-1 h-full rounded-l-2xl"
                  style={{ backgroundColor: it.accent }}
                  variants={{
                    rest: { scaleY: 0, originY: 0 },
                    hovered: { scaleY: 1, originY: 0 },
                  }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                />

                {/* Soft accent wash */}
                <motion.div
                  className="absolute inset-0 rounded-2xl pointer-events-none"
                  style={{ background: `radial-gradient(ellipse at top left, ${it.accent}10 0%, transparent 70%)` }}
                  variants={{
                    rest: { opacity: 0 },
                    hovered: { opacity: 1 },
                  }}
                  transition={{ duration: 0.35 }}
                />

                <div className="relative p-6 flex flex-col items-center text-center gap-3">
                  {/* Icon circle */}
                  <motion.div
                    className="h-14 w-14 rounded-full border-2 flex items-center justify-center"
                    style={{ borderColor: `${it.accent}40`, color: it.accent }}
                    variants={{
                      rest: {
                        scale: 1,
                        borderColor: `${it.accent}40`,
                        boxShadow: '0 0 0px transparent',
                      },
                      hovered: {
                        scale: 1.15,
                        borderColor: it.accent,
                        boxShadow: `0 0 20px ${it.accent}50`,
                      },
                    }}
                    transition={{ type: 'spring', stiffness: 320, damping: 18 }}
                  >
                    <motion.div
                      variants={{
                        rest: { rotate: 0 },
                        hovered: { rotate: 12 },
                      }}
                      transition={{ type: 'spring', stiffness: 260, damping: 16 }}
                    >
                      <Icon className="h-6 w-6" />
                    </motion.div>
                  </motion.div>

                  {/* Title */}
                  <h3 className="text-base font-semibold text-navy leading-snug">
                    {it.title}
                  </h3>

                  {/* Description */}
                  <p className="text-xs text-slatebody leading-relaxed">
                    {it.desc}
                  </p>

                  {/* Learn More — slides up on hover */}
                  <motion.span
                    className="inline-flex items-center gap-1 text-xs font-semibold"
                    style={{ color: it.accent }}
                    variants={{
                      rest: { opacity: 0, y: 8 },
                      hovered: { opacity: 1, y: 0 },
                    }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}
                  >
                    Learn More <ArrowRight className="h-3 w-3" />
                  </motion.span>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* ── Center-aligned Explore button ── */}
        <div className="flex justify-center mt-10">
          <Link to="/industries">
            <PrimaryButton icon={<ArrowRight className="h-4 w-4" />}>
              {exploreLabel}
            </PrimaryButton>
          </Link>
        </div>
      </div>
    </section>
  );
}
